import type { ReactNode } from "react";
import ExamFilters from "src/components/NewComponents/Creator/Filters/ExamFilters";
import { AiDiscussionsFilter } from "src/components/NewComponents/Filters/AiDiscussionsFilter";
import CommentsNotificationsFilter from "src/components/NewComponents/Filters/CommentsNotificationsFilter";
import SpecialisationsFilter from "src/components/NewComponents/Filters/SpecialisationsFilter";
import isPes from "src/helpers/isPes";
import { useDepartments, useGetCemVersions } from "src/hooks/useDepartments";
import { useTerms } from "src/hooks/useTerms";
import { isStaff } from "src/hooks/useUser";

type ExamDetails = Record<string, unknown> & {
	aiDiscussions: { isAiUnapproved: boolean; hasUnresolvedReviews: boolean };
};

type ExamFiltersContainerProps = {
	examDetails: ExamDetails;
	setExamDetails: (details: ExamDetails) => void;
	/** Widoczne sekcje (jak allowList w ExamFilters) + "notifications"; brak = wszystkie. */
	allowList?: string[];
};

type RenderFilter = (
	expanded: number | null,
	handleChange: (id: number) => void,
) => ReactNode;

export function ExamFiltersContainer({
	examDetails,
	setExamDetails,
	allowList,
}: ExamFiltersContainerProps) {
	const { isLoading: isTermsLoading, data: terms } = useTerms();
	const { isLoading: isDepartmentsLoading, data: departments } =
		useDepartments();
	const { isLoading: isCemVersionLoading, data: cemVersions } =
		useGetCemVersions();

	const renderNotificationsList: RenderFilter = (expanded, handleChange) => {
		if (!isStaff() || (allowList && !allowList.includes("notifications"))) {
			return null;
		}

		return (
			<CommentsNotificationsFilter
				selected={!!examDetails.commentsNotifications}
				selectedData={examDetails}
				setSelectedData={(newData: ExamDetails) =>
					setExamDetails({ ...examDetails, ...newData })
				}
				expanded={expanded === 6}
				onChange={() => handleChange(6)}
			/>
		);
	};

	const renderSpecialisationsFilter: RenderFilter = (
		expanded,
		handleChange,
	) => {
		if (!isStaff() || !isPes()) {
			return null;
		}

		return (
			<SpecialisationsFilter
				expanded={expanded === 7}
				onChange={() => handleChange(7)}
			/>
		);
	};

	const renderAiDiscussionsFilter: RenderFilter = (expanded, handleChange) => {
		if (!isStaff()) {
			return null;
		}

		const { isAiUnapproved, hasUnresolvedReviews } =
			examDetails.aiDiscussions || {};

		return (
			<AiDiscussionsFilter
				selected={!!isAiUnapproved || !!hasUnresolvedReviews}
				selectedData={examDetails}
				setSelectedData={(newData: ExamDetails) =>
					setExamDetails({ ...examDetails, ...newData })
				}
				expanded={expanded === 9}
				onChange={() => handleChange(9)}
			/>
		);
	};

	const renderAdminFilters: RenderFilter = (expanded, handleChange) => {
		return (
			<>
				{renderNotificationsList(expanded, handleChange)}
				{renderAiDiscussionsFilter(expanded, handleChange)}
				{renderSpecialisationsFilter(expanded, handleChange)}
			</>
		);
	};

	return (
		<ExamFilters
			examDetails={examDetails}
			setData={setExamDetails}
			departments={departments}
			terms={terms}
			cemVersions={cemVersions}
			isLoading={isTermsLoading || isDepartmentsLoading || isCemVersionLoading}
			renderExtraFilters={renderAdminFilters}
			allowList={allowList}
		/>
	);
}
